import { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUTH_BG = "https://customer-assets.emergentagent.com/job_artist-portal-ldc/artifacts/tzd4nnry_ChatGPT%20Image%20Mar%2020%2C%202026%2C%2007_40_40%20PM.png";

export default function AuthPage({ onLogin }) {
  const [step, setStep] = useState("email"); // email | create | login | not_found
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [artistName, setArtistName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const handleForgot = async () => {
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setResetSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send reset email. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailCheck = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/check-email`, { email: email.trim() });
      if (!data.found) {
        setStep("not_found");
      } else if (data.has_account) {
        setArtistName(data.artist_name || "");
        setStep("login");
      } else {
        setArtistName(data.artist_name || "");
        setStep("create");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error checking email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/register`, {
        email: email.trim(),
        password,
      });
      localStorage.setItem("ldc_token", data.token);
      localStorage.setItem("ldc_email", data.email);
      onLogin(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        email: email.trim(),
        password,
      });
      localStorage.setItem("ldc_token", data.token);
      localStorage.setItem("ldc_email", data.email);
      onLogin(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep("email");
    setPassword("");
    setConfirmPassword("");
    setError("");
    setResetSent(false);
  };

  return (
    <div className="auth-page">
      {/* Left Panel - Background Image */}
      <div className="auth-left" style={{ backgroundImage: `url(${AUTH_BG})` }}>
        <div className="auth-left-overlay" />
        <div className="auth-left-content">
          <img
            src="https://customer-assets.emergentagent.com/job_artist-portal-ldc/artifacts/39dze3hq_Text%20Logo%20Gold_noBG_WEBP.webp"
            alt="London Drumstick Co."
            className="auth-logo-img"
          />
          <p className="auth-tagline">ARTIST PORTAL</p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="auth-right">
        <div className="auth-form-container">

          {/* Logo for mobile */}
          <img
            src="https://customer-assets.emergentagent.com/job_artist-portal-ldc/artifacts/39dze3hq_Text%20Logo%20Gold_noBG_WEBP.webp"
            alt="London Drumstick Co."
            className="auth-logo-mobile"
          />

          {/* STEP: EMAIL */}
          {step === "email" && (
            <form onSubmit={handleEmailCheck} data-testid="email-form">
              <h1 className="auth-title">ARTIST PORTAL</h1>
              <p className="auth-subtitle">Enter your email address to continue</p>
              {error && <div className="auth-error" data-testid="email-error">{error}</div>}
              <div className="form-group">
                <label className="form-label">EMAIL ADDRESS</label>
                <input
                  data-testid="email-input"
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  autoFocus
                />
              </div>
              <button
                data-testid="continue-btn"
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? "CHECKING..." : "CONTINUE"}
              </button>
            </form>
          )}

          {/* STEP: EMAIL NOT FOUND */}
          {step === "not_found" && (
            <div data-testid="not-found-section">
              <h1 className="auth-title">ACCESS DENIED</h1>
              <div className="warning-box" data-testid="not-found-warning">
                <div className="warning-icon">!</div>
                <div>
                  <p className="warning-title">EMAIL NOT FOUND</p>
                  <p className="warning-text">
                    The email address <strong>{email}</strong> was not found in our artist database.
                    Please contact London Drumstick Company to be added as a sponsored artist.
                  </p>
                </div>
              </div>
              <button
                data-testid="try-again-btn"
                className="btn-secondary"
                onClick={goBack}
              >
                TRY ANOTHER EMAIL
              </button>
            </div>
          )}

          {/* STEP: CREATE ACCOUNT */}
          {step === "create" && (
            <form onSubmit={handleRegister} data-testid="register-form">
              <h1 className="auth-title">
                {artistName ? `WELCOME,` : "CREATE ACCOUNT"}
              </h1>
              {artistName && <p className="auth-name-display">{artistName.toUpperCase()}</p>}
              <p className="auth-subtitle">Create a password for your artist portal</p>
              {error && <div className="auth-error" data-testid="register-error">{error}</div>}
              <div className="form-group">
                <label className="form-label">PASSWORD</label>
                <input
                  data-testid="password-input"
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                  minLength={8}
                />
              </div>
              <div className="form-group">
                <label className="form-label">CONFIRM PASSWORD</label>
                <input
                  data-testid="confirm-password-input"
                  type="password"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  required
                />
              </div>
              <button
                data-testid="create-account-btn"
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? "CREATING..." : "CREATE ACCOUNT"}
              </button>
              <button type="button" className="btn-ghost" onClick={goBack}>
                BACK
              </button>
            </form>
          )}

          {/* STEP: LOGIN */}
          {step === "login" && (
            <form onSubmit={handleLogin} data-testid="login-form">
              <h1 className="auth-title">WELCOME BACK</h1>
              {artistName && <p className="auth-name-display">{artistName.toUpperCase()}</p>}
              <p className="auth-subtitle">Enter your password to access your portal</p>
              {error && <div className="auth-error" data-testid="login-error">{error}</div>}
              <div className="form-group">
                <label className="form-label">PASSWORD</label>
                <input
                  data-testid="login-password-input"
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  required
                  autoFocus
                />
              </div>
              <button
                data-testid="login-submit-btn"
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? "LOGGING IN..." : "LOGIN"}
              </button>
              {resetSent ? (
                <p className="auth-reset-sent" data-testid="reset-sent-msg">
                  If that email is registered, a reset link has been sent. Check your inbox.
                </p>
              ) : (
                <button
                  type="button"
                  className="btn-link"
                  onClick={handleForgot}
                  disabled={loading}
                  data-testid="forgot-password-btn"
                >
                  FORGOT PASSWORD?
                </button>
              )}
              <button type="button" className="btn-ghost" onClick={goBack}>
                BACK
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
